import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { mediaApi } from './api';
import { keys } from '@/lib/query/keys';
import { MediaBrowser } from './MediaBrowser';
import { PageHeader, MonoValue } from '@/lib/ui/Typography';
import { Dialog } from '@/lib/ui/Dialog';
import { Tabs } from '@/lib/ui/Tabs';
import { Button } from '@/lib/ui/Button';
import { TextInput } from '@/lib/ui/Field';
import { Switch } from '@/lib/ui/Switch';
import { Tag } from '@/lib/ui/Tag';
import { I18nField } from '@/lib/forms/widgets/I18nField';
import { ConfirmDialog } from '@/lib/ui/ConfirmDialog';
import { DataView } from '@/lib/ui/states';
import { useToast } from '@/lib/ui';
import { useApiError } from '@/lib/query/useApiError';
import { useGate } from '@/lib/rbac/Gate';
import { isAppError } from '@/lib/api/errors';
import { formatBytes, formatDateTime } from '@/lib/format';
import type { I18n, MediaAsset } from '@/lib/api/types';

export function MediaLibraryPage() {
  const { t } = useTranslation();
  const [active, setActive] = useState<MediaAsset | null>(null);
  return (
    <div>
      <PageHeader eyebrow={t('media.eyebrow')} title={t('media.libraryTitle')} />
      <MediaBrowser canUpload onSelect={setActive} />
      {active && (
        <AssetDetailDialog asset={active} open onClose={() => setActive(null)} />
      )}
    </div>
  );
}

function AssetDetailDialog({
  asset,
  open,
  onClose,
}: {
  asset: MediaAsset;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const onError = useApiError();
  const { allowed: canManage } = useGate('media:manage');

  const [alt, setAlt] = useState<I18n>(asset.alt);
  const [title, setTitle] = useState<I18n>(asset.title);
  const [caption, setCaption] = useState<I18n>(asset.caption);
  const [credit, setCredit] = useState(asset.credit);
  const [folder, setFolder] = useState(asset.folder);
  const [tags, setTags] = useState(asset.tags.join(', '));
  const [restricted, setRestricted] = useState(asset.is_restricted);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [blockingRefs, setBlockingRefs] = useState<string[] | null>(null);

  const save = useMutation({
    mutationFn: () =>
      mediaApi.update(asset.id, {
        alt,
        title,
        caption,
        credit,
        folder,
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
        is_restricted: restricted,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.media.all() });
      toast.success(t('media.savedToast'));
    },
    onError: (e) => onError(e),
  });

  const remove = useMutation({
    mutationFn: () => mediaApi.remove(asset.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.media.all() });
      toast.success(t('media.deletedToast'));
      onClose();
    },
    // Media delete hard-blocks with 409 + blocking references in error.details (gap 17).
    onError: (e) => {
      if (isAppError(e) && e.is('conflict')) {
        setBlockingRefs((e.details ?? []).map((d) => d.message));
        setConfirmDelete(false);
      } else onError(e);
    },
  });

  const detailTab = (
    <div className="flex flex-col gap-md">
      <div className="flex items-center gap-md">
        {asset.kind === 'image' && !asset.is_restricted ? (
          <img src={asset.url} alt={asset.alt.en} className="h-[120px] w-[120px] rounded-md object-cover" />
        ) : (
          <div className="flex h-[120px] w-[120px] items-center justify-center rounded-md bg-canvas-deep-sand text-[2rem]">
            📄
          </div>
        )}
        <dl className="grid grid-cols-2 gap-x-lg gap-y-xs text-small">
          <dt className="text-ink-mute">{t('media.kind')}</dt>
          <dd><Tag variant="neutral">{asset.kind}</Tag></dd>
          <dt className="text-ink-mute">{t('media.size')}</dt>
          <dd><MonoValue>{formatBytes(asset.size_bytes)}</MonoValue></dd>
          {asset.width > 0 && (
            <>
              <dt className="text-ink-mute">{t('media.dimensions')}</dt>
              <dd><MonoValue>{asset.width}×{asset.height}</MonoValue></dd>
            </>
          )}
          <dt className="text-ink-mute">{t('media.uploaded')}</dt>
          <dd>{formatDateTime(asset.created_at)}</dd>
          <dt className="text-ink-mute">{t('media.checksum')}</dt>
          <dd className="truncate"><MonoValue className="text-label-sm">{asset.checksum.slice(0, 16)}…</MonoValue></dd>
        </dl>
      </div>
      <I18nField label={t('media.altText')} value={alt} onChange={setAlt} disabled={!canManage} />
      <I18nField label={t('common.title')} value={title} onChange={setTitle} disabled={!canManage} />
      <I18nField label={t('media.caption')} value={caption} onChange={setCaption} disabled={!canManage} />
      <TextInput label={t('media.credit')} value={credit} onChange={(e) => setCredit(e.target.value)} disabled={!canManage} />
      <TextInput label={t('media.folder')} value={folder} onChange={(e) => setFolder(e.target.value)} disabled={!canManage} />
      <TextInput label={t('media.tagsLabel')} value={tags} onChange={(e) => setTags(e.target.value)} disabled={!canManage} />
      <Switch
        label={t('media.restrictedAsset')}
        hint={t('media.restrictedHint')}
        checked={restricted}
        onCheckedChange={setRestricted}
        disabled={!canManage}
      />
    </div>
  );

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(o) => !o && onClose()}
        title={asset.file_name}
        size="lg"
        footer={
          canManage ? (
            <>
              <Button variant="ghost" onClick={() => setConfirmDelete(true)}>
                {t('common.delete')}
              </Button>
              <Button loading={save.isPending} onClick={() => save.mutate(undefined)}>
                {t('common.save')}
              </Button>
            </>
          ) : undefined
        }
      >
        <Tabs
          tabs={[
            { value: 'detail', label: t('media.tabDetails'), content: detailTab },
            { value: 'usages', label: t('media.tabUsages'), content: <UsagesTab id={asset.id} /> },
          ]}
        />
        {blockingRefs && (
          <div className="mt-md rounded-md border-[0.5px] border-signal-error/40 bg-signal-error-fill p-md">
            <p className="text-small font-medium text-signal-error">
              {t('media.cannotDeleteInUse')}
            </p>
            <ul className="mt-xs list-disc ps-lg text-small text-ink-graphite">
              {blockingRefs.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
            <p className="mt-xs text-small text-ink-mid">
              {t('media.removeOrSwapRefs')}
            </p>
          </div>
        )}
      </Dialog>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t('media.deleteAssetTitle')}
        description={t('media.deleteAssetDescription')}
        tone="danger"
        confirmLabel={t('common.delete')}
        loading={remove.isPending}
        onConfirm={() => remove.mutate(undefined)}
      />
    </>
  );
}

function UsagesTab({ id }: { id: string }) {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: keys.media.usages(id),
    queryFn: () => mediaApi.usages(id),
  });
  return (
    <DataView
      query={query}
      // An unreferenced asset can come back as null/undefined (not just []); guard the
      // length read so the Usages tab shows the empty state instead of crashing the app.
      isEmpty={(d) => !d || d.length === 0}
      empty={<p className="text-small text-ink-mute">{t('media.notReferencedSafe')}</p>}
    >
      {(usages) => (
        <ul className="divide-y divide-hairline rounded-md border-[0.5px] border-hairline">
          {(usages ?? []).map((u, i) => (
            <li key={i} className="flex items-center justify-between px-md py-sm text-small">
              <span className="font-mono text-ink-deep">{u.entity_type}</span>
              <MonoValue className="text-label-sm text-ink-mute">
                {u.entity_id} · {u.field}
              </MonoValue>
            </li>
          ))}
        </ul>
      )}
    </DataView>
  );
}
